// ─── TermsPage ────────────────────────────────────────────────────────────────
// Public "Terms and Conditions of Service" page, linked from the footer's
// Policy column. Mirrors the Help Center page's chrome convention: the
// marketing-site <Navigation /> (blue top bar) always, the FONDRIEST
// <AppHeader /> additionally when logged in, and the shared <Footer /> —
// so it's intentionally outside ProtectedRoute / MainLayout and reads for
// logged-out visitors too.
//
// Content is held as a plain SECTIONS array so the legal copy is easy to
// edit in one place; each section renders a numbered heading plus its
// paragraphs and/or (optionally bold-led) bullet points.

import { Navigation } from '../components/Navigation'
import { AppHeader } from '../components/AppHeader'
import { Footer } from '../components/Footer'
import useAuthStore from '../store/auth-store'

const SECTIONS = [
  {
    n: 1, title: 'Acceptance of Terms',
    paras: [
      'By creating an account, accessing, or using the WQData LIVE platform, you agree to be bound by these Terms and Conditions. If you are agreeing to these terms on behalf of a company, government agency, or research institution, you represent that you have the authority to bind that entity.',
    ],
  },
  {
    n: 2, title: 'Description of Service',
    paras: [
      'WQData LIVE is a cloud-based project management and environmental data hosting service. It allows users to collect, visualize, and manage environmental data transmitted from NexSens X-Series, G3-Series, and legacy data logging platforms via Wi-Fi, cellular, or satellite telemetry.',
    ],
  },
  {
    n: 3, title: 'Subscription Tiers & Billing',
    bullets: [
      { lead: 'Service Levels:', text: 'Services are provisioned in tiers, including Basic (Free), Advanced, Professional, and Enterprise.' },
      { lead: 'Fees:', text: 'Free tiers are subject to data storage and rate limitations. Paid subscription tiers are subject to the terms of your specific procurement agreement or purchase order with NexSens or authorized distributors.' },
      { lead: 'Telemetry Costs:', text: 'Telemetry configurations (e.g., higher frequency cellular or Iridium satellite transmit intervals) directly impact cellular data plan consumption and power demands. Users are solely responsible for managing transmit intervals and offsetting associated data overages.' },
    ],
  },
  {
    n: 4, title: 'User Accounts and Role-Based Access',
    bullets: [
      { lead: 'Account Creation:', text: 'Users must provide accurate profile details and select the proper local time zone during setup to ensure accurate data timestamps across dashboards and reports.' },
      { lead: 'Security:', text: 'Accounts are password-protected. Users are entirely responsible for maintaining the confidentiality of login credentials.' },
      { lead: 'Access Hierarchy:', text: 'WQData LIVE utilizes multi-level, role-based access. Project Administrators possess full permissions for remote configuration, system modifications, sensor commands, and user invitation. Collaborators and public viewers are restricted to data viewing, standard report generation, and exporting functions.' },
    ],
  },
  {
    n: 5, title: 'Data Ownership and Permitted Use',
    bullets: [
      { lead: 'Customer Data:', text: 'You retain all intellectual property rights to the environmental measurement data uploaded by your data loggers to WQData LIVE.' },
      { lead: 'License to Provider:', text: 'You grant NexSens Technology, Inc. a non-exclusive, worldwide, royalty-free license to host, transmit, cache, back up, and display your data solely to deliver the service.' },
      { lead: 'Public Portals:', text: 'If an Administrator configures a public portal, slideshow presentation, or public applet link, that specific project data becomes accessible to the public. NexSens is not responsible for public misuse of open-access data.' },
    ],
  },
  {
    n: 6, title: 'Acceptable Use and Restrictions',
    paras: ['You agree not to:'],
    bullets: [
      { text: 'Reverse engineer, decompile, or extract the source code of WQData LIVE or its REST APIs.' },
      { text: "Use the system's automated alarm notifications (Email/SMS) for critical life-safety applications where a delayed or missed network packet could result in physical injury or environmental disaster." },
      { text: 'Bypass API rate limiting or inject malicious code into the public project portals or media galleries.' },
    ],
  },
  {
    n: 7, title: 'Hardware Integration and Remote Configuration',
    bullets: [
      { lead: 'Telemetry Failures:', text: 'WQData LIVE coordinates remote commands (e.g., changing logging intervals or enabling parameter flags). Remote commands are queued and execute at the logger’s next scheduled transmission window unless manually forced on-site via local configuration software. NexSens is not liable for delayed configurations caused by localized cellular or satellite network outages.' },
      { lead: 'Parameter Backlogs:', text: 'Re-enabling previously disabled parameter transmission flags may prompt a data logger to upload its entire internal memory record. Except on Iridium satellite networks, this can trigger significant data transmission overhead, for which the customer assumes financial responsibility.' },
    ],
  },
  {
    n: 8, title: 'Disclaimer of Warranties',
    paras: [
      'WQData LIVE is provided on an "As-Is" and "As-Available" basis. NexSens Technology, Inc. explicitly disclaims all warranties, express or implied, including the implied warranties of merchantability, fitness for a particular purpose, and non-infringement. NexSens does not guarantee that data transmission will be uninterrupted, error-free, or completely secure from telemetry signal interference.',
    ],
  },
  {
    n: 9, title: 'Limitation of Liability',
    paras: [
      'To the maximum extent permitted by law, NexSens Technology, Inc. shall not be liable for any indirect, incidental, special, consequential, or punitive damages. This includes, without limitation, loss of telemetry data, equipment corrosion damage, faulty environmental readings, fines levied by regulatory bodies, or cost of deployment vessel rentals and field troubleshooting hours. NexSens\' total aggregate liability for all claims arising out of this service shall not exceed the total fees paid by the user for the WQData LIVE service tier in the twelve (12) months preceding the incident.',
    ],
  },
  {
    n: 10, title: 'Termination',
    bullets: [
      { lead: 'By User:', text: 'You may stop using the service or close your account at any time by coordinating written notification with NexSens Support (wqdatalive.com).' },
      { lead: 'By NexSens:', text: 'NexSens reserves the right to suspend or terminate accounts associated with non-payment of paid subscription tiers, system abuse, or extended periods of telemetry inactivity on free tiers.' },
    ],
  },
  {
    n: 11, title: 'Governing Law',
    paras: [
      'These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of Ohio, USA, without regard to conflict of law principles. Any legal action or proceeding arising under these terms shall be brought exclusively in the courts located within Greene County, Ohio.',
    ],
  },
]

export default function TermsPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return (
    <div className="w-full bg-white min-h-screen flex flex-col">
      <Navigation />
      {isAuthenticated && <AppHeader />}

      <main className="flex-1 bg-[#F1F2F4] py-12">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <header className="mb-8">
            <h1 className="text-3xl font-bold text-[#141414]">
              Terms and Conditions of Service
            </h1>
            <p className="mt-1 text-lg text-[#505050]">WQData LIVE</p>
            <div className="border-b border-gray-300 mt-4" />
          </header>

          <article className="space-y-8">
            {SECTIONS.map((s) => (
              <section key={s.n}>
                <h2 className="text-xl font-bold text-[#141414] mb-3">
                  {s.n}. {s.title}
                </h2>

                {(s.paras || []).map((p, i) => (
                  <p key={i} className="text-[15px] leading-relaxed text-[#4D4D4D] mb-3">
                    {p}
                  </p>
                ))}

                {s.bullets && (
                  <ul className="list-disc pl-6 space-y-2 marker:text-gray-400">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="text-[15px] leading-relaxed text-[#4D4D4D]">
                        {b.lead && <span className="font-semibold text-[#141414]">{b.lead} </span>}
                        {b.text}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  )
}
